// Bridge to EventLive for Mac (macos/ in this repo). The Mac app hosts this
// web build in a WKWebView and injects `window.EventLiveMac`; going live
// happens in its native Studio window (AVFoundation + RTMPS, seamless camera
// and microphone switching) instead of the browser engine.
//
// Contract (see macos/EventLive/Web/NativeBridge.swift):
//   window.EventLiveMac.call(type, payload) -> Promise
//   window "eventlivemac" CustomEvents: { type, streamKey, ... }
import { useEffect, useRef } from "react";
import { makeUrl, eventTitle } from "@/components/globalUtils";
import { getLanguage } from "@/localization";
import { logUploadToken } from "../../utils/logUploadToken";

const RTMPS_SERVER = "rtmps://go.eventlive.pro:443/live";

export const isMacApp = () =>
  typeof window !== "undefined" && Boolean(window.EventLiveMac?.isMacApp);

// Latest EventLive for Mac disk image (eventlive.pro redirects to it).
export const MAC_DOWNLOAD_URL = "https://www.eventlive.pro/mac/download";

// A browser on a Mac, where EventLive for Mac can be offered. iPads report
// "Macintosh" too, but have a touch screen.
export const isMacBrowser = () =>
  typeof navigator !== "undefined" &&
  /Macintosh|Mac OS X/.test(navigator.userAgent || "") &&
  (navigator.maxTouchPoints || 0) <= 1 &&
  !isMacApp();

// Downloads the Mac app in place: the page stays open while it downloads.
export const downloadMacApp = () => {
  window.location.assign(MAC_DOWNLOAD_URL);
};

const call = (type, payload) => window.EventLiveMac.call(type, payload);

// Same fields the phone apps get from openStreamWindow (components/Utils.js).
const studioParams = (event, customDomain) => ({
  streamKey: event.stream_key,
  rtmpurl: `${RTMPS_SERVER}/${event.stream_key}`,
  title: eventTitle(event) || "",
  is_test: Boolean(event.is_test),
  url: makeUrl(event, customDomain),
  slug: event.slug,
  broadcaster: event.broadcaster,
  event_status: event.event_status,
  liveViewers: parseInt(event.liveViewers, 10) || 0,
  // The Studio shows its own words in the app's language ("de", "es-MX"…);
  // apps from before this ignore it.
  language: getLanguage(),
});

// Opens (or brings forward) the Studio for this event. Resolves true when
// the studio is open, false when the Mac app refused (e.g. another event is
// already live — it tells the user itself).
export async function openMacStudio(event, customDomain) {
  if (!isMacApp() || !event?.stream_key) return false;
  try {
    // The Mac app sends it with the session log when the studio closes.
    const token = await logUploadToken();
    await call("openStudio", {
      ...studioParams(event, customDomain),
      ...(token ? { logUploadToken: token } : {}),
    });
    return true;
  } catch (error) {
    if (String(error?.message || error).includes("another_event_live"))
      return false;
    throw error;
  }
}

// Keeps the studio's title/viewer count/status current from the event
// subscription the page already has.
export function updateMacStudioEvent(event) {
  if (!isMacApp() || !event?.stream_key) return;
  call("updateStudioEvent", {
    streamKey: event.stream_key,
    title: eventTitle(event),
    event_status: event.event_status,
    liveViewers: parseInt(event.liveViewers, 10) || 0,
  }).catch(() => {});
}

// Subscribes to studio events (studioStatus, studioClosed). Returns an
// unsubscribe function.
export function onMacStudioEvent(handler) {
  if (typeof window === "undefined") return () => {};
  const listener = (e) => handler(e.detail || {});
  window.addEventListener("eventlivemac", listener);
  return () => window.removeEventListener("eventlivemac", listener);
}

// Calls `onClosed(detail)` when the Studio window for `streamKey` closes —
// the Mac counterpart of the phone apps' "StreamDone" event.
// detail.wentLive is true when the stream actually went live.
export function useMacStudioClosed(streamKey, onClosed) {
  const handlerRef = useRef(onClosed);
  handlerRef.current = onClosed;
  useEffect(() => {
    if (!isMacApp() || !streamKey) return undefined;
    return onMacStudioEvent((detail) => {
      if (detail.type === "studioClosed" && detail.streamKey === streamKey) {
        handlerRef.current?.(detail);
      }
    });
  }, [streamKey]);
}
